import { Fragment, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { BsTrash } from "react-icons/bs";
import { FiEdit2, FiCheck, FiX } from "react-icons/fi";
import Confirm from "../../../../../../../Components/Popups/Confirm";
import {
  updateRoleData,
  deleteRoleData,
} from "../../../../../../../services/settings.services";
import {
  groupState,
  hasChildRows,
  indeterminateRef,
  toggleChild,
  toggleGroup,
  visibleChildren,
} from "./rolePermissions";

const SKELETON_COLS = 3;
const SKELETON_PARENTS = 5;
const SKELETON_CHILDREN = [2, 3, 2, 4, 2];

const Bone = ({ className = "" }) => (
  <div className={`bg-ui-border rounded animate-pulse ${className}`} />
);

const ManageRolesSkeleton = () => (
  <div className="overflow-hidden border border-ui-border rounded-xl">
    <table className="border-collapse text-sm w-full">
      <thead>
        <tr className="bg-ui-surface-sunken border-b border-ui-border">
          <th className="px-5 py-3 min-w-[280px] border-r border-ui-border">
            <Bone className="h-3 w-24" />
          </th>
          {Array.from({ length: SKELETON_COLS }).map((_, i) => (
            <th key={i} className="px-5 py-3 min-w-[180px] border-r border-ui-border last:border-r-0">
              <div className="flex items-center justify-center gap-2">
                <Bone className="h-3 w-20" />
                <Bone className="h-3 w-3 rounded" />
                <Bone className="h-3 w-3 rounded" />
              </div>
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {Array.from({ length: SKELETON_PARENTS }).map((_, pi) => (
          <Fragment key={`sp-${pi}`}>
            <tr className="bg-ui-surface-sunken border-b border-ui-border">
              <td className="px-5 py-2.5 border-r border-ui-border">
                <Bone className="h-3 w-40" />
              </td>
              {Array.from({ length: SKELETON_COLS }).map((_, ci) => (
                <td key={ci} className="px-5 py-2.5 text-center border-r border-ui-border last:border-r-0">
                  <Bone className="h-3.5 w-3.5 rounded mx-auto" />
                </td>
              ))}
            </tr>
            {Array.from({ length: SKELETON_CHILDREN[pi] ?? 2 }).map((_, chi) => (
              <tr key={`sc-${pi}-${chi}`} className="border-b border-ui-surface-sunken">
                <td className="pl-10 pr-5 py-2 border-r border-ui-border">
                  <Bone className="h-2.5 w-32" />
                </td>
                {Array.from({ length: SKELETON_COLS }).map((_, ci) => (
                  <td key={ci} className="px-5 py-2 text-center border-r border-ui-border last:border-r-0">
                    <Bone className="h-3.5 w-3.5 rounded mx-auto" />
                  </td>
                ))}
              </tr>
            ))}
          </Fragment>
        ))}
      </tbody>
    </table>
  </div>
);

const ManageRoles = ({ roles, permisions, setRoles, setSnack, loading }) => {
  const { t } = useTranslation("settings");
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleteRole, setDeleteRole] = useState(null);
  const [editingIndex, setEditingIndex] = useState(-1);
  const [editedText, setEditedText] = useState({});
  const [savingIndex, setSavingIndex] = useState(-1);

  // Saves the role; the change shows at once and is undone if it can't be saved.
  const updateData = async (role, permissions, before) => {
    const saved = await updateRoleData({ name: role.name, permissions }, role.id);
    if (!saved.error) {
      setRoles((prev) =>
        prev.map((r) => (r.id === role.id ? { ...r, name: role.name } : r))
      );
      setSnack((s) => ({ ...s, open: true, text: t("roles.updated"), severity: "success" }));
    } else {
      if (before) {
        setRoles((prev) =>
          prev.map((r) => (r.id === role.id ? { ...r, permissions: before } : r))
        );
      }
      setSnack((s) => ({
        ...s,
        open: true,
        text: saved?.error?.response?.data?.message ?? t("common:messages.somethingWentWrong"),
        severity: "error",
      }));
    }
  };

  const changePermissions = async (role, change) => {
    const before = role.permissions;
    const updated = { ...role, permissions: change(before) };
    setRoles((prev) => prev.map((r) => (r.id === role.id ? updated : r)));
    await updateData(updated, updated.permissions, before);
  };

  const parentPermissions = (role, group, e) => {
    const on = e.target.checked;
    return changePermissions(role, (selected) => toggleGroup(selected, group, on));
  };

  const childPermissions = (role, group, child, e) => {
    const on = e.target.checked;
    return changePermissions(role, (selected) => toggleChild(selected, group, child, on));
  };

  const handleTextClick = (index, role) => {
    setEditingIndex(index);
    setEditedText({ index, name: role.name, permissions: role.permissions, id: role.id });
  };

  const handleSave = async () => {
    const idx = editingIndex;
    setSavingIndex(idx);
    setEditingIndex(-1);
    await updateData(editedText, editedText.permissions);
    setSavingIndex(-1);
  };

  const handleCancel = () => {
    setEditingIndex(-1);
    setEditedText({});
  };

  const confirmDeleteRole = async () => {
    setConfirmDelete(false);
    const saved = await deleteRoleData(deleteRole.id);
    if (!saved.error) {
      setRoles((prev) => prev.filter((r) => r.id !== deleteRole.id));
      setDeleteRole(null);
      setSnack((s) => ({ ...s, open: true, text: t("roles.deleted"), severity: "success" }));
    } else {
      setSnack((s) => ({
        ...s,
        open: true,
        text: saved?.error?.response?.data?.message ?? t("common:messages.somethingWentWrong"),
        severity: "error",
      }));
    }
  };

  if (loading) return <ManageRolesSkeleton />;

  return (
    <>
      <Confirm
        open={confirmDelete}
        setOpen={setConfirmDelete}
        handleConfirm={confirmDeleteRole}
        text={
          <Trans
            t={t}
            i18nKey="roles.deleteConfirm"
            values={{ name: deleteRole?.name }}
            components={{ b: <b /> }}
          />
        }
      />

      <div className="overflow-auto border border-ui-border rounded-xl [&::-webkit-scrollbar]:h-1 [&::-webkit-scrollbar-thumb]:bg-ui-border [&::-webkit-scrollbar-thumb:hover]:bg-ui-text-subtle">
        <table className="border-collapse text-sm" style={{ minWidth: "max-content", width: "100%" }}>

          {/* Header */}
          <thead>
            <tr className="bg-ui-surface-sunken border-b border-ui-border">
              <th className="sticky left-0 z-10 bg-ui-surface-sunken text-left px-5 py-3 text-xs font-semibold text-ui-text-muted uppercase tracking-wider min-w-[280px] border-r border-ui-border">
                {t("roles.permissions")}
              </th>
              {roles.map((role, roleIndex) => (
                <th
                  key={roleIndex}
                  className="px-3 py-3 min-w-[220px] text-center border-r border-ui-border last:border-r-0"
                >
                  {editingIndex === roleIndex ? (
                    <div className="inline-flex items-center gap-0 border border-brand ring-1 ring-brand-light rounded-lg overflow-hidden bg-ui-surface w-[200px]">
                      <input
                        autoFocus
                        className="flex-1 min-w-0 text-center text-sm font-medium text-ui-text bg-transparent px-2 py-1.5 outline-none"
                        value={editedText.name}
                        onChange={(e) => setEditedText((prev) => ({ ...prev, name: e.target.value }))}
                        onKeyDown={(e) => { if (e.key === "Enter") handleSave(); if (e.key === "Escape") handleCancel(); }}
                      />
                      <button onClick={handleSave} className="px-1.5 py-1.5 text-state-success hover:text-state-success hover:bg-state-success-bg transition-colors border-l border-brand-light" title={t("common:actions.save")}>
                        <FiCheck size={13} />
                      </button>
                      <button onClick={handleCancel} className="px-1.5 py-1.5 text-red-400 hover:text-state-danger hover:bg-state-danger-bg transition-colors border-l border-brand-light" title={t("common:actions.cancel")}>
                        <FiX size={13} />
                      </button>
                    </div>
                  ) : savingIndex === roleIndex ? (
                    <span className="text-xs text-ui-text-subtle italic">{t("roles.saving")}</span>
                  ) : (
                    <div className="flex items-center justify-center gap-2">
                      <span className="text-xs font-semibold text-ui-text">{role.name}</span>
                      <button
                        onClick={() => handleTextClick(roleIndex, role)}
                        className="text-ui-text-subtle hover:text-brand transition-colors"
                        title={t("roles.rename")}
                      >
                        <FiEdit2 size={12} />
                      </button>
                      <button
                        onClick={() => { setConfirmDelete(true); setDeleteRole(role); }}
                        className="text-ui-text-subtle hover:text-state-danger transition-colors"
                        title={t("common:actions.delete")}
                      >
                        <BsTrash size={12} />
                      </button>
                    </div>
                  )}
                </th>
              ))}
            </tr>
          </thead>

          {/* Body */}
          <tbody>
            {permisions?.map((data) => (
              <Fragment key={data.id}>
                {/* Group row: the whole module */}
                <tr className="bg-ui-surface-sunken border-b border-ui-border">
                  <td className="sticky left-0 z-10 bg-ui-surface-sunken px-5 py-2.5 font-semibold text-ui-text border-r border-ui-border">
                    {data.description}
                  </td>
                  {roles.map((role) => {
                    const { checked, indeterminate } = groupState(role.permissions, data);
                    return (
                      <td key={role.id} className="px-5 py-2.5 text-center border-r border-ui-border last:border-r-0">
                        <input
                          type="checkbox"
                          className="accent-brand cursor-pointer"
                          aria-label={t("roles.permissionAria", { role: role.name, permission: data.description })}
                          checked={checked}
                          ref={indeterminateRef(indeterminate)}
                          onChange={(e) => parentPermissions(role, data, e)}
                        />
                      </td>
                    );
                  })}
                </tr>

                {/* Child rows, for groups with more than one permission showing */}
                {hasChildRows(data) &&
                  visibleChildren(data).map((child) => (
                    <tr key={child.id} className="border-b border-ui-surface-sunken">
                      <td className="sticky left-0 z-10 bg-ui-surface pl-10 pr-5 py-2 text-xs text-ui-text-muted border-r border-ui-border">
                        {child.description}
                      </td>
                      {roles.map((role) => (
                        <td key={role.id} className="px-5 py-2 text-center border-r border-ui-border last:border-r-0">
                          <input
                            type="checkbox"
                            className="accent-brand cursor-pointer"
                            aria-label={t("roles.permissionAria", { role: role.name, permission: child.description })}
                            checked={role.permissions.includes(child.id)}
                            onChange={(e) => childPermissions(role, data, child, e)}
                          />
                        </td>
                      ))}
                    </tr>
                  ))}
              </Fragment>
            ))}
          </tbody>

        </table>
      </div>
    </>
  );
};

export default ManageRoles;
